/**
 * A crown health screen as its page reads it: the tile archive beside `crown_health.gpkg` (the
 * map draws the crowns, their points and the hotspots from it; the GeoPackage itself is never
 * read, it holds every index of every crown) and the counts of `crown_health.json`.
 */
import { useEffect, useState } from 'react';
import type { LinkSource } from '@/map/linkSource';
import { openPmtiles, type Archive } from '@/map/pmtiles';
import { CROWN_HEALTH_FILE, parseCrownHealth, type CrownHealthSummary } from './summary';

export type HealthArchive = { state: 'off' | 'checking' | 'none' } | { state: 'ready'; archive: Archive };
export type HealthSummary = { state: 'off' | 'loading' | 'failed' } | { state: 'ready'; summary: CrownHealthSummary };

/** The screen's archive, opened once a session; `none` when the bucket has none beside the GeoPackage. */
export function useHealthArchive(screen: { object: string; path: string } | undefined, links: LinkSource): HealthArchive {
  const key = screen?.path ? JSON.stringify([screen.object, screen.path]) : '';
  const [held, setHeld] = useState<{ key: string; result: HealthArchive } | null>(null);
  useEffect(() => {
    if (!key) return;
    const [object, path] = JSON.parse(key) as [string, string];
    let alive = true;
    void openPmtiles(object, path, links)
      .then((archive): HealthArchive => (archive ? { state: 'ready', archive } : { state: 'none' }))
      .catch((): HealthArchive => ({ state: 'none' }))
      .then((result) => alive && setHeld({ key, result }));
    return () => {
      alive = false;
    };
  }, [key, links]);
  if (!key) return { state: 'off' };
  if (!held || held.key !== key) return { state: 'checking' };
  return held.result;
}

/** The screen's `crown_health.json`; `failed` when it cannot be fetched or is not one. */
export function useHealthSummary(object: string | undefined, links: LinkSource): HealthSummary {
  const [held, setHeld] = useState<{ object: string; result: HealthSummary } | null>(null);
  useEffect(() => {
    if (!object) return;
    let alive = true;
    void links
      .url(object, CROWN_HEALTH_FILE)
      .then((url) => fetch(url))
      .then((r) => (r.ok ? r.json() : null))
      .then((raw: unknown): HealthSummary => {
        const summary = parseCrownHealth(raw);
        return summary ? { state: 'ready', summary } : { state: 'failed' };
      })
      .catch((): HealthSummary => ({ state: 'failed' }))
      .then((result) => alive && setHeld({ object, result }));
    return () => {
      alive = false;
    };
  }, [object, links]);
  if (!object) return { state: 'off' };
  if (!held || held.object !== object) return { state: 'loading' };
  return held.result;
}
